import React from "react";
import { Container, Row, Col } from "reactstrap";
import styles from "./GuidelineSupport.module.scss";
import FAQ from "../../../common/FAQ/FAQ";

const stepData = [
    {
        title: "How to Create a Brand",
        steps: [
            {
                step: "Preparation",
                subSteps: [
                    "Research: Understand your target audience and market.",
                    "Brand Concept: Define your brand's mission, vision, and values.",
                    "Design Elements: Develop a logo, color scheme, and other visual elements.",
                ],
            },
            {
                step: "Submission",
                subSteps: [
                    "Basic Information: Provide the brand name, logo, category, and website.",
                    "Documentation: Upload necessary documents such as a business license and trademark registration.",
                    "Brand Relationship: Describe your relationship with the brand, including any partnership agreements.",
                    "Customization: Add a brand story and highlight key products.",
                ],
            },
            {
                step: "Approval",
                subSteps: [
                    "Verification: The platform will review the submitted information and documents.",
                    "Feedback: If additional information is required, the platform will contact you.",
                    "Final Approval: Once verified, the brand will be approved and listed on the platform.",
                ],
            },
        ],
    },
    {
        title: "Documentation Requirements",
        steps: [
            "Business License: Proof of legal business operation.",
            "Trademark Registration: Confirmation of brand ownership.",
            "Partnership Agreements: Documentation of any partnerships or distributor agreements.",
            "Additional Documents: Any other relevant legal or business documents.",
        ],
    },
    {
        title: "Approval Process",
        steps: [
            {
                step: "Initial Review",
                subSteps: [
                    "Submission Check: Ensure all required fields and documents are completed.",
                    "Initial Verification: Basic verification of documents and information.",
                ],
            },
            {
                step: "Detailed Verification",
                subSteps: [
                    "In-depth Review: Detailed examination of the provided documents.",
                    "Clarifications: Requests for any additional information or clarifications.",
                ],
            },
            {
                step: "Final Decision",
                subSteps: [
                    "Approval: Brand is approved and can be listed.",
                    "Rejection: If the brand does not meet requirements, feedback will be provided for resubmission.",
                ],
            },
        ],
    },
];

const faqData = [
    {
        question: "What is React?",
        answer: "React is a JavaScript library for building user interfaces.",
    },
    {
        question: "What is Reactstrap?",
        answer: "Reactstrap is a React library for using Bootstrap components.",
    },
    {
        question: "How do I install Bootstrap in React?",
        answer: "You can install Bootstrap via npm or include it in the HTML.",
    },
];

const contactSupportData = [
    {
        title: "Email",
        details: [
            { label: "Support Email", value: "support@maelano.com" },
            { label: "Response Time", value: "Typically within 24-48 hours" },
        ],
    },
    {
        title: "Phone",
        details: [
            { label: "Support Phone Number", value: "+1-810-123-4567" },
            {
                label: "Availability",
                value: "Monday to Friday, 9 AM to 5 PM (Local Time)",
            },
        ],
    },
    {
        title: "Live Chat",
        details: [
            { label: "Link to Live Chat", value: "Start live chat" },
            { label: "Availability", value: "24/7 support for real-time assistance" },
        ],
    },
];

const GuidelineSupport = () => {
    return (
        <div className={styles.guidelineSupportContainer}>
            <h4 className={styles.mainHeading}>Guidelines and Support</h4>

            <h5 className={styles.sectionGuidelineHeading}>
                Guidelines for Brand Creation
            </h5>
            <Row className={styles.guidelineRow}>
                <Col xl="6" className={styles.createBrandColumn}>
                    <div className={styles.box}>
                        <h5 className={styles.sectionHeading}>{stepData[0].title}</h5>
                        <h6 className={styles.subSectionHeading}>Step-by-Step Process:</h6>
                        <ol className={styles.stepList}>
                            {stepData[0].steps.map((step, i) => (
                                <li key={i}>
                                    <h6 className={styles.subSectionHeading}>{step.step}</h6>
                                    <ul className={styles.subStepsList}>
                                        {step.subSteps.map((sub, j) => (
                                            <li key={j}>{sub}</li>
                                        ))}
                                    </ul>
                                </li>
                            ))}
                        </ol>
                    </div>
                </Col>

                <Col xl="6" className={styles.documentationApprovalColumn}>
                    <Row className={styles.documentationApprovalRow}>
                        <Col md="12" className={styles.documentationColumn}>
                            <div className={styles.box}>
                                <h5 className={styles.sectionHeading}>{stepData[1].title}</h5>
                                <ul>
                                    {stepData[1].steps.map((step, i) => (
                                        <li key={i}>{step}</li>
                                    ))}
                                </ul>
                            </div>
                        </Col>
                        <Col md="12" className={styles.approvalProcessColumn}>
                            <div className={styles.box}>
                                <h5 className={styles.sectionHeading}>{stepData[2].title}</h5>
                                <h6 className={styles.subSectionHeading}>
                                    Stages of Approval:
                                </h6>
                                <ol className={styles.stepList}>
                                    {stepData[2].steps.map((step, i) => (
                                        <li key={i}>
                                            <h6 className={styles.subSectionHeading}>{step.step}</h6>
                                            <ul className={styles.subStepsList}>
                                                {step.subSteps.map((sub, j) => (
                                                    <li key={j}>{sub}</li>
                                                ))}
                                            </ul>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        </Col>
                    </Row>
                </Col>
            </Row>

            <h5 className={styles.sectionGuidelineHeading}>
                Frequently Asked Questions (FAQ’s)
            </h5>

            <Container fluid className={styles.faqDataContainer}>
                <FAQ faqs={faqData} />
            </Container>

            <h5 className={styles.sectionGuidelineHeading}>Contact Support</h5>

            <Container fluid className={styles.contactSupportContainer}>
                <Row className={styles.contactSupportRow}>
                    {contactSupportData?.map((contact, index) => (
                        <Col key={index} xxl={6} className={styles.contactSupportColumn}>
                            <div className={styles.contactDetailContainer}>
                                <p className={styles.contactTitle}>{contact.title}:</p>
                                <div className={styles.contactDetails}>
                                    {contact.details?.map((dataItem, dataIndex) => (
                                        <Row key={dataIndex} className={styles.contactContentRow}>
                                            <Col sm={6} xl={4}>
                                                {dataItem.label}:
                                            </Col>
                                            <Col className={styles.contactContentValue}>
                                                {dataItem.value}
                                            </Col>
                                        </Row>
                                    ))}
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>
            </Container>
        </div>
    );
};

export default GuidelineSupport;